/*
 * Globe Inventio — projection orthographique dessinée à la main.
 *
 * Aucune bibliothèque : l'atlas est un fichier de thème (contours Natural Earth simplifiés,
 * topologie conservée), et la projection tient en trois produits scalaires par point.
 *
 * Pourquoi des vecteurs 3D plutôt que des sinus par point : chaque point du monde est
 * converti une seule fois au chargement en vecteur unitaire. À chaque image on ne calcule
 * plus que trois produits scalaires contre la base (centre, est, nord) — soit environ
 * 2 700 points × 9 multiplications, très en dessous d'une image même sur un vieux mobile.
 *
 * Pourquoi le trait plutôt que le remplissage pour les terres : un polygone qui traverse
 * l'horizon demande de longer le limbe pour se refermer proprement. En trait on coupe
 * simplement le chemin, et le rendu au trait colle de toute façon à l'identité du site.
 */
(function () {
  'use strict';

  var DEG = Math.PI / 180;

  // Quelques noms français courants : le marchand ne devrait pas avoir à connaître
  // l'orthographe anglaise de l'atlas pour poser une épingle.
  var ALIASES = {
    japon: 'japan', maroc: 'morocco', tunisie: 'tunisia', algerie: 'algeria',
    turquie: 'turkey', mexique: 'mexico', grece: 'greece', inde: 'india',
    perou: 'peru', espagne: 'spain', italie: 'italy', egypte: 'egypt',
    indonesie: 'indonesia', bresil: 'brazil', chine: 'china', coree: 'south korea',
    thailande: 'thailand', vietnam: 'vietnam', senegal: 'senegal', ghana: 'ghana',
    kenya: 'kenya', afriquedusud: 'south africa', colombie: 'colombia',
    argentine: 'argentina', chili: 'chile', portugal: 'portugal',
    ethiopie: 'ethiopia', mali: 'mali', libye: 'libya', jordanie: 'jordan',
    liban: 'lebanon', oman: 'oman', philippines: 'philippines', malaisie: 'malaysia'
  };

  function slug(s) {
    return String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function toVec(lon, lat) {
    var a = lon * DEG, b = lat * DEG, c = Math.cos(b);
    return [c * Math.cos(a), c * Math.sin(a), Math.sin(b)];
  }

  function Globe(root) {
    this.root = root;
    this.canvas = root.querySelector('.world-globe__canvas');
    this.ctx = this.canvas.getContext('2d');
    this.panels = Array.prototype.slice.call(root.querySelectorAll('.world-panel'));
    this.chips = Array.prototype.slice.call(root.querySelectorAll('.world-chip'));
    this.status = root.querySelector('.world-globe__live');

    var raw = root.querySelector('.world-globe__data');
    this.worlds = raw ? JSON.parse(raw.textContent) : [];

    this.lambda = -20;   // lacet, en degrés
    this.phi = 14;       // tangage, en degrés
    this.target = null;
    this.selected = -1;
    this.spin = root.dataset.autorotate === 'true';
    this.spun = 0;
    this.lastPaint = 0;
    this.onScreen = true;
    this.dirty = true;
    this.raf = 0;
    this.atlas = null;

    this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.calm.matches) this.spin = false;

    this.bind();
    this.watch();
  }

  Globe.prototype.watch = function () {
    // Rien ne se télécharge avant que le globe approche de l'écran : sur une page
    // longue, un visiteur qui ne descend pas ne paie jamais l'atlas.
    var self = this;
    if (!('IntersectionObserver' in window)) return this.load();
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        io.disconnect();
        self.load();
      }
    }, { rootMargin: '400px' });
    io.observe(this.root);
  };

  Globe.prototype.load = function () {
    var self = this;
    fetch(this.root.dataset.atlas, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (data) { self.prepare(data); })
      .catch(function () {
        // L'atlas est décoratif : sans lui, la liste des mondes reste lisible.
        self.root.classList.add('world-globe--flat');
      });
  };

  Globe.prototype.prepare = function (data) {
    var e = data.e || 10, arcs = data.a, i, j;

    // Dé-delta-encodage puis conversion en vecteurs, une seule fois.
    this.arcs = new Array(arcs.length);
    this.arcPts = new Array(arcs.length);
    for (i = 0; i < arcs.length; i++) {
      var flat = arcs[i], x = 0, y = 0, pts = [], vecs = [];
      for (j = 0; j < flat.length; j += 2) {
        x += flat[j]; y += flat[j + 1];
        var lon = x / e, lat = y / e;
        pts.push([lon, lat]);
        vecs.push(toVec(lon, lat));
      }
      this.arcPts[i] = pts;
      this.arcs[i] = vecs;
    }

    this.names = data.n;
    this.shapes = data.c;
    this.centroids = data.k;

    // Nom (ou code) -> identifiant de l'atlas.
    this.index = {};
    for (var cid in this.names) {
      if (Object.prototype.hasOwnProperty.call(this.names, cid)) {
        this.index[slug(this.names[cid])] = cid;
        this.index[cid] = cid;
      }
    }

    this.graticule = this.buildGraticule();
    this.resolve();

    // Avant tout calcul de taille : tant que la classe manque, la CSS garde le globe en
    // display:none pour le repli sans script, et un élément masqué mesure zéro.
    this.root.classList.add('is-ready');
    this.sizeCanvas();

    var self = this;
    var pending = 0;
    // Un seul chemin de remesure, appelé par les deux sources : le ResizeObserver suit la
    // colonne (donc l'ouverture d'un menu ou un changement de police), l'écouteur de fenêtre
    // couvre la rotation de l'appareil et les navigateurs sans ResizeObserver.
    this.remeasure = function () {
      if (pending) return;
      pending = requestAnimationFrame(function () {
        pending = 0;
        self.sizeCanvas();
        if (self.dirty) self.tick();
      });
    };
    this.ro = ('ResizeObserver' in window) ? new ResizeObserver(this.remeasure) : null;
    if (this.ro) this.ro.observe(this.canvas.parentNode);
    window.addEventListener('resize', this.remeasure);
    window.addEventListener('orientationchange', this.remeasure);

    var revive = function () { self.dirty = true; self.remeasure(); self.tick(); };

    // Le défilement est un état, pas un événement : on le tient pendant le geste et on
    // relance une image 120 ms après la dernière secousse.
    var settle = 0;
    this.scrolling = false;
    window.addEventListener('scroll', function () {
      self.scrolling = true;
      clearTimeout(settle);
      settle = setTimeout(function () {
        self.scrolling = false;
        self.dirty = true;
        self.tick();
      }, 120);
      self.tick();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.onScreen = entries.some(function (e) { return e.isIntersecting; });
        if (self.onScreen) revive();
      }, { rootMargin: '200px' }).observe(this.root);
    }

    // Retour d'onglet et retour arrière depuis le cache de session : deux autres moments
    // où le navigateur peut rendre un canvas vide.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) revive();
    });
    window.addEventListener('pageshow', revive);

    this.select(0, false);
    this.tick();
  };

  Globe.prototype.buildGraticule = function () {
    var lines = [], lon, lat, pts;
    for (lon = -180; lon < 180; lon += 30) {
      pts = [];
      for (lat = -90; lat <= 90; lat += 4) pts.push(toVec(lon, lat));
      lines.push(pts);
    }
    for (lat = -60; lat <= 60; lat += 30) {
      pts = [];
      for (lon = -180; lon <= 180; lon += 4) pts.push(toVec(lon, lat));
      lines.push(pts);
    }
    return lines;
  };

  // Chaque monde nomme un ou plusieurs pays ; on retient leurs anneaux et un point d'ancrage.
  Globe.prototype.resolve = function () {
    var self = this, unknown = [];
    this.worlds.forEach(function (w) {
      w.rings = [];
      var lonSum = 0, latSum = 0, count = 0;
      String(w.countries || '').split(',').forEach(function (raw) {
        var key = slug(raw);
        if (!key) return;
        if (ALIASES[key]) key = slug(ALIASES[key]);
        var cid = self.index[key];
        if (!cid) { unknown.push(raw.trim()); return; }
        var polys = self.shapes[cid] || [];
        polys.forEach(function (poly) {
          poly.forEach(function (ring) { w.rings.push(self.ringVecs(ring)); });
        });
        var k = self.centroids[cid];
        if (k) { lonSum += k[0]; latSum += k[1]; count++; }
      });
      w.anchor = count ? [lonSum / count, latSum / count] : [0, 0];
      w.anchorVec = toVec(w.anchor[0], w.anchor[1]);
    });
    if (unknown.length && window.console) {
      console.warn('[Globe Inventio] pays introuvables dans l’atlas : ' + unknown.join(', '));
    }
  };

  Globe.prototype.ringVecs = function (ring) {
    var out = [], self = this;
    ring.forEach(function (idx) {
      var arc = self.arcs[idx < 0 ? ~idx : idx];
      var seq = idx < 0 ? arc.slice().reverse() : arc;
      for (var i = out.length ? 1 : 0; i < seq.length; i++) out.push(seq[i]);
    });
    return out;
  };

  Globe.prototype.sizeCanvas = function () {
    var box = this.canvas.parentNode.getBoundingClientRect();
    var side = Math.max(120, Math.min(box.width, box.height || box.width));
    // Au-delà de 2 la densité ne se voit plus mais le coût de remplissage, si.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var px = Math.round(side * dpr);

    // Écrire canvas.width efface le dessin — même en y remettant la valeur qu'il avait
    // déjà. Or sur iOS le repli de la barre d'adresse émet un resize à chaque défilement :
    // sans ce garde-fou, le globe s'efface à chaque geste. C'est le clignotement.
    if (this.canvas.width !== px || this.canvas.height !== px) {
      this.canvas.width = px;
      this.canvas.height = px;
      this.dirty = true;
    }

    this.side = side;
    this.canvas.style.width = side + 'px';
    this.canvas.style.height = side + 'px';
    // Redimensionner remet le contexte à zéro ; sinon la transformation est déjà en place
    // et la réécrire ne coûte rien.
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.radius = side / 2 - Math.max(6, side * 0.03);
    this.cx = side / 2;
    this.cy = side / 2;
  };

  // Base orthonormée du point de vue : centre, est, nord.
  Globe.prototype.basis = function () {
    var e = toVec(this.lambda, this.phi);
    var h = Math.hypot(-e[1], e[0]) || 1;
    var east = [-e[1] / h, e[0] / h, 0];
    var north = [
      e[1] * east[2] - e[2] * east[1],
      e[2] * east[0] - e[0] * east[2],
      e[0] * east[1] - e[1] * east[0]
    ];
    return { e: e, east: east, north: north };
  };

  Globe.prototype.strokeChain = function (pts, b) {
    var ctx = this.ctx, R = this.radius, cx = this.cx, cy = this.cy;
    var open = false, prev = null, prevD = 0;
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i];
      var d = p[0] * b.e[0] + p[1] * b.e[1] + p[2] * b.e[2];
      if (d > 0) {
        if (!open && prev) {
          // On rentre dans le champ : on part du point exact où l'arc coupe l'horizon.
          var q = this.crossing(prev, p, prevD, d);
          ctx.moveTo(cx + this.dot(q, b.east) * R, cy - this.dot(q, b.north) * R);
          open = true;
          ctx.lineTo(cx + this.dot(p, b.east) * R, cy - this.dot(p, b.north) * R);
        } else if (!open) {
          ctx.moveTo(cx + this.dot(p, b.east) * R, cy - this.dot(p, b.north) * R);
          open = true;
        } else {
          ctx.lineTo(cx + this.dot(p, b.east) * R, cy - this.dot(p, b.north) * R);
        }
      } else if (open) {
        var r = this.crossing(prev, p, prevD, d);
        ctx.lineTo(cx + this.dot(r, b.east) * R, cy - this.dot(r, b.north) * R);
        open = false;
      }
      prev = p; prevD = d;
    }
  };

  Globe.prototype.dot = function (p, q) { return p[0] * q[0] + p[1] * q[1] + p[2] * q[2]; };

  // Le canvas dessine dans un repère de `side` pixels, mais la CSS peut l'afficher plus
  // petit (max-width). Tout ce qui vient de l'écran doit repasser par ce facteur.
  Globe.prototype.scale = function (box) {
    var shown = (box || this.canvas.getBoundingClientRect()).width;
    return shown > 0 ? this.side / shown : 1;
  };

  // Le produit scalaire varie linéairement le long de la corde : le point d'annulation
  // se calcule donc d'un coup, sans dichotomie, puis se reprojette sur la sphère.
  Globe.prototype.crossing = function (a, c, da, dc) {
    var t = da / (da - dc);
    var v = [a[0] + (c[0] - a[0]) * t, a[1] + (c[1] - a[1]) * t, a[2] + (c[2] - a[2]) * t];
    var n = Math.hypot(v[0], v[1], v[2]) || 1;
    return [v[0] / n, v[1] / n, v[2] / n];
  };

  Globe.prototype.styles = function () {
    var cs = getComputedStyle(this.canvas);
    return {
      ocean: cs.getPropertyValue('--globe-ocean').trim(),
      grid: cs.getPropertyValue('--globe-grid').trim(),
      land: cs.getPropertyValue('--globe-land').trim(),
      pin: cs.getPropertyValue('--globe-pin').trim(),
      active: cs.getPropertyValue('--globe-active').trim(),
      soon: cs.getPropertyValue('--globe-soon').trim()
    };
  };

  Globe.prototype.draw = function () {
    // Les écouteurs sont posés dès la construction, mais l'atlas arrive par le réseau.
    // Toute interaction dans cet intervalle demanderait de dessiner des contours qui
    // n'existent pas encore.
    if (!this.arcs) return;

    var ctx = this.ctx, b = this.basis(), c = this.styles();
    var R = this.radius, cx = this.cx, cy = this.cy, i;

    ctx.clearRect(0, 0, this.side, this.side);

    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fillStyle = c.ocean;
    ctx.fill();

    ctx.lineWidth = 1;
    ctx.strokeStyle = c.grid;
    ctx.beginPath();
    for (i = 0; i < this.graticule.length; i++) this.strokeChain(this.graticule[i], b);
    ctx.stroke();

    ctx.lineWidth = 1;
    ctx.strokeStyle = c.land;
    ctx.beginPath();
    for (i = 0; i < this.arcs.length; i++) this.strokeChain(this.arcs[i], b);
    ctx.stroke();

    // Les pays d'un monde : remplis quand le monde est ouvert, en pointillé sinon.
    for (i = 0; i < this.worlds.length; i++) {
      var w = this.worlds[i];
      w.sx = null;
      var facing = this.dot(w.anchorVec, b.e);
      if (facing <= 0.02) continue;
      // Près du limbe, la projection écrase le pays contre le bord : sans fondu il reste
      // un éclat blanc étiré, illisible et faux à l'œil. On le fait disparaître.
      var fade = Math.min(1, (facing - 0.02) / 0.3);
      var on = i === this.selected;
      var soon = w.status !== 'unlocked';
      ctx.globalAlpha = fade;

      ctx.beginPath();
      for (var r = 0; r < w.rings.length; r++) this.strokeChain(w.rings[r], b);
      if (soon) {
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = on ? 1.6 : 1.2;
        ctx.strokeStyle = on ? c.active : c.soon;
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        ctx.fillStyle = on ? c.active : c.pin;
        ctx.fill();
        ctx.lineWidth = on ? 1.6 : 1;
        ctx.strokeStyle = on ? c.active : c.pin;
        ctx.stroke();
      }

      var px = cx + this.dot(w.anchorVec, b.east) * R;
      var py = cy - this.dot(w.anchorVec, b.north) * R;
      // Une épingle trop pâle ne doit pas rester cliquable : on ne l'enregistre
      // comme cible que lorsqu'elle est franchement visible.
      if (fade > 0.35) { w.sx = px; w.sy = py; }

      ctx.beginPath();
      ctx.arc(px, py, on ? 5 : 3, 0, Math.PI * 2);
      ctx.fillStyle = on ? c.active : (soon ? c.soon : c.pin);
      ctx.fill();

      if (on) {
        ctx.beginPath();
        ctx.arc(px, py, 11, 0, Math.PI * 2);
        ctx.lineWidth = 1;
        ctx.strokeStyle = c.active;
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
    }
  };

  Globe.prototype.tick = function () {
    var self = this;
    if (this.raf) return;
    var step = function (ts) {
      self.raf = 0;
      var moving = false;
      if (!ts) ts = 0;

      // Pendant un défilement, on ne peint rien du tout. Repeindre un canvas pendant que
      // le compositeur fait glisser la page, c'est lui disputer le même travail : le
      // défilement accroche. Le globe reprend là où il en était dès que le geste s'arrête.
      if (self.scrolling && !self.dragging) {
        if (self.onScreen) self.raf = requestAnimationFrame(step);
        return;
      }

      if (self.target) {
        var dl = ((self.target[0] - self.lambda + 540) % 360) - 180;
        var dp = self.target[1] - self.phi;
        if (Math.abs(dl) < 0.15 && Math.abs(dp) < 0.15) {
          self.lambda = self.target[0]; self.phi = self.target[1]; self.target = null;
        } else {
          self.lambda += dl * 0.12; self.phi += dp * 0.12; moving = true;
        }
      } else if (self.spin && !self.dragging) {
        // La Terre tourne vers l'est : vue de l'extérieur, nord en haut, la surface
        // défile vers la droite — donc la longitude visée diminue.
        self.lambda -= 0.12; self.spun += 0.12; moving = true;
        // Un demi-tour suffit à montrer que le globe tourne. Au-delà, ce n'est plus une
        // invitation, c'est une animation permanente qui vide la batterie d'un téléphone.
        if (self.spun > 180) self.spin = false;
      }

      if (moving || self.dirty) {
        self.dirty = false;
        self.lastPaint = ts;
        self.draw();
      }
      // On garde la boucle vivante tant que le globe est à l'écran. iOS libère le tampon
      // d'un canvas sorti du champ : au retour il est vide, et sans une image de plus il
      // resterait blanc — c'est le globe qui « apparaît et disparaît » au défilement.
      // Une image sans redessin ne coûte que la comparaison ci-dessus.
      if (moving || self.dragging || self.onScreen) self.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  };

  Globe.prototype.bind = function () {
    var self = this;
    var canvas = this.canvas;
    var start = null, moved = 0;

    canvas.addEventListener('pointerdown', function (ev) {
      // On ne coupe pas la rotation ici : poser le doigt pour défiler la page ne doit pas
      // compter comme une prise en main. C'est le premier vrai déplacement qui la coupe.
      self.target = null;
      self.dragging = true;
      self.touch = ev.pointerType === 'touch';
      moved = 0;
      start = { x: ev.clientX, y: ev.clientY, l: self.lambda, p: self.phi };
      // La capture sert à la souris, qui peut sortir du canvas en cours de glissé. Un
      // pointeur tactile est déjà capturé implicitement, et la capturer explicitement
      // empêcherait le navigateur d'abandonner le geste pour défiler la page.
      if (!self.touch && canvas.setPointerCapture) canvas.setPointerCapture(ev.pointerId);
      self.tick();
    });

    canvas.addEventListener('pointermove', function (ev) {
      if (!self.dragging || !start) return;
      var sc = self.scale();
      var dx = (ev.clientX - start.x) * sc, dy = (ev.clientY - start.y) * sc;
      moved = Math.max(moved, Math.hypot(ev.clientX - start.x, ev.clientY - start.y));
      if (moved > 4) self.spin = false;
      // 180° pour un balayage d'un diamètre. Les signes sont inversés par rapport à la
      // longitude visée : augmenter lambda déplace la surface vers la gauche, or le doigt
      // doit entraîner la surface avec lui. On tire le globe, on ne déplace pas la caméra.
      var k = 180 / (self.radius * 2);
      self.lambda = start.l - dx * k;
      self.phi = Math.max(-80, Math.min(80, start.p + dy * k));

      // Le geste n'arrive jusqu'ici que si le navigateur ne l'a pas pris pour un
      // défilement. Avec touch-action: pan-y il ne garde que les gestes qui partent vers
      // le haut ou le bas ; un geste qui part de côté nous appartient entièrement, y
      // compris sa composante verticale. D'où : le doigt tourne le globe dans les deux
      // sens, et le balayage vertical continue de défiler la page.
      self.dirty = true;
      self.tick();
    });

    var release = function (ev) {
      if (!self.dragging) return;
      self.dragging = false;
      if (moved < 6) self.pick(ev);
      self.tick();
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', function () { self.dragging = false; });

    canvas.addEventListener('keydown', function (ev) {
      var k = ev.key, s = ev.shiftKey ? 20 : 8;
      // Une flèche pousse le globe du même côté qu'un doigt : un seul modèle mental.
      if (k === 'ArrowLeft') self.lambda += s;
      else if (k === 'ArrowRight') self.lambda -= s;
      else if (k === 'ArrowUp') self.phi = Math.max(-80, self.phi - s);
      else if (k === 'ArrowDown') self.phi = Math.min(80, self.phi + s);
      else return;
      ev.preventDefault();
      self.spin = false; self.target = null; self.dirty = true; self.tick();
    });

    this.chips.forEach(function (chip, i) {
      chip.addEventListener('click', function () { self.spin = false; self.select(i, true); });
    });
  };

  // On vise l'épingle la plus proche plutôt que le polygone du pays : au doigt, un pays
  // comme la Tunisie fait quelques pixels, l'épingle reste toujours atteignable.
  Globe.prototype.pick = function (ev) {
    var box = this.canvas.getBoundingClientRect();
    var sc = this.scale(box);
    var x = (ev.clientX - box.left) * sc, y = (ev.clientY - box.top) * sc;
    // 30 px au doigt, quelle que soit l'échelle d'affichage.
    var best = -1, bestD = 30 * sc;
    for (var i = 0; i < this.worlds.length; i++) {
      var w = this.worlds[i];
      if (w.sx == null) continue;
      var d = Math.hypot(w.sx - x, w.sy - y);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best >= 0) this.select(best, true);
  };

  Globe.prototype.select = function (i, turn) {
    if (i < 0 || i >= this.worlds.length) return;
    this.selected = i;
    var w = this.worlds[i];

    this.panels.forEach(function (p, j) { p.classList.toggle('is-selected', j === i); });
    this.chips.forEach(function (c, j) {
      var on = j === i;
      c.classList.toggle('is-selected', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this.status) this.status.textContent = w.name || '';

    if (turn) {
      if (this.calm.matches) {
        this.lambda = w.anchor[0]; this.phi = w.anchor[1]; this.target = null;
      } else {
        this.target = [w.anchor[0], w.anchor[1]];
      }
      this.bringIntoView(i);
    } else {
      this.lambda = w.anchor[0]; this.phi = w.anchor[1];
    }
    this.dirty = true;
    this.tick();
  };

  // Sur un téléphone, le globe occupe le haut de l'écran et la fiche commence sous le pli.
  // Choisir un pays sans rien voir changer donne l'impression que rien ne s'est passé.
  //
  // On amène donc la fiche à hauteur de lecture — mais pas tout en haut : elle se pose au
  // milieu de l'écran, de sorte que le bas du globe et les pastilles restent visibles
  // au-dessus. On garde ainsi la carte et sa légende dans le même regard.
  //
  // Et on ne bouge rien si la fiche est déjà lisible : voler le défilement de quelqu'un
  // qui lit est pire que de ne rien faire.
  Globe.prototype.bringIntoView = function (i) {
    var panel = this.panels[i];
    if (!panel || !window.matchMedia('(max-width: 899px)').matches) return;

    var chips = this.root.querySelector('.world-globe__chips');
    var sous = chips ? chips.getBoundingClientRect().bottom : 0;
    var haut = panel.getBoundingClientRect().top;

    var cachee = haut < sous + 4;                       // passée sous les pastilles
    // 260 px, c'est la place qu'il faut pour lire l'étiquette, le nom, le lieu et les
    // premières lignes du texte. En dessous, la fiche est présente mais pas lisible — et
    // sur un petit écran c'est exactement le cas qui obligeait à faire défiler à la main.
    var tropBas = haut > window.innerHeight - 260;
    if (!cachee && !tropBas) return;

    var y = haut + window.scrollY - window.innerHeight * 0.45;

    // On ne suppose pas que le défilement doux existe : `scrollBehavior` dans le style est
    // le test de capacité fiable. Sans lui — ou si le visiteur demande moins d'animation —
    // on saute directement, ce qui reste juste, seulement moins joli.
    var doux = 'scrollBehavior' in document.documentElement.style && !this.calm.matches;

    if (doux) {
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.scrollTo(0, y);
    }
  };

  var boot = function () {
    var nodes = document.querySelectorAll('.world-globe[data-atlas]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].dataset.booted) { nodes[i].dataset.booted = '1'; new Globe(nodes[i]); }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Le client de la personnalisation recrée la section sans recharger la page.
  document.addEventListener('shopify:section:load', boot);
})();
